import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

import { API } from "@/lib/api";
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Coś poszło nie tak. Spróbuj ponownie.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, {..}=logged in, false=guest
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip /me check if session_id in hash — AuthCallback handles that
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      setUser(res.data);
      return { ok: true, user: res.data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, []);

  const register = useCallback(async (email, password, name) => {
    try {
      const res = await axios.post(`${API}/auth/register`, { email, password, name });
      setUser(res.data);
      return { ok: true, user: res.data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      /* ignore */
    }
    setUser(false);
  }, []);

  const exchangeSession = useCallback(async (session_id) => {
    try {
      const res = await axios.post(`${API}/auth/session`, { session_id });
      setUser(res.data);
      return { ok: true, user: res.data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, exchangeSession, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
